
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    
import React from "react";
import {
  getPathsFromContext,
  getResourceByPath,
  getResourceCollectionFromContext,
} from "next-drupal";
import Layout from "components/Layout";
import { DrupalJsonApiParams } from "drupal-jsonapi-params";
import getCrumbs from "lib/getCrumbs";
import { getGlobals } from "lib/getGlobals";
import Header from "components/Header";
import fixPathsSlug from "lib/fixPathsSlug";
import Meta from "components/Meta";
import NodeEventContent from "components/Event/NodeEventContent";
import classNames from "lib/classNames";
import { formatDateYMD, splitDate } from "lib/formatDate";
import { ChevronRightIcon } from "@heroicons/react/outline";
import Link from "next/link";
import { getPathFromContext } from "lib/i18n";
import useTranslation from "next-translate/useTranslation";
import dynamic from "next/dynamic";

const NodeEventList = dynamic(() => import("components/Event/NodeEventList"));

export default function EventSlugPage({ globals, crumbs, node, nodes }) {
  const { lang, t } = useTranslation("common");

  if (!node) return null;
  const hasImage = node.field_image || false;
  const dateStart = splitDate(node.field_date_start, lang);
  const dateEnd = node.field_date_end
    ? splitDate(node.field_date_end, lang)
    : null;
  const hasNodes = nodes.length > 0;

  let date = t("utils.date.simple", {
    date: `${dateStart.day} ${dateStart.month} ${dateStart.year}`,
  });

  if (dateEnd && JSON.stringify(dateStart) !== JSON.stringify(dateEnd)) {
    date = t("utils.date.from_to", {
      start:
        dateStart.month !== dateEnd.month
          ? `${dateStart.day} ${dateStart.month}`
          : dateStart.day,
      end: `${dateEnd.day} ${dateEnd.month} ${dateEnd.year}`,
    });
  }

  return (
    <Layout globals={globals}>
      <Meta
        title={node.field_meta_title || node.title}
        description={
          node.body?.summary || null
        }
      />
      <Header
        crumbs={crumbs}
        title={node.title}
        subtitle={
          <>
            {node.field_type && (
              <span className="px-3 py-1.5 bg-primary text-sm text-primary-darker uppercase mr-8">
                {node.field_type.name}
              </span>
            )}
            <span>{date}</span>
          </>
        }
        notTranslated={node.field_not_translated}
      />

      <section className={classNames("relative py-20", hasImage && "pt-10")}>
        {hasImage && (
          <div
            className="absolute top-0 inset-x-0 h-32 bg-secondary-dark"
            aria-hidden="true"
          />
        )}
        <div className="relative container mx-auto px-4 lg:max-w-screen-lg">
          <NodeEventContent node={node} />
        </div>
      </section>

      {hasNodes && (
        <div className="py-20 bg-secondary-light border-b border-secondary border-opacity-20">
          <div className="relative container mx-auto px-4">
            <div className="mb-20 flex flex-col md:flex-row md:items-center gap-4">
              <h2 className="text-3xl font-serif">{t("event.others.title")}</h2>

              <div>
                <ChevronRightIcon className="inline-block h-4 w-4 mr-2" />
                <Link href={t("event.path")} passHref>
                  <a className="underline decoration-primary underline-offset-4 hover:text-primary transition-colors">
                    {t("event.others.button")}
                  </a>
                </Link>
              </div>
            </div>
            <NodeEventList nodes={nodes} theme="borderless" />
          </div>
        </div>
      )}
    </Layout>
  );
}

export async function getStaticPaths(context) {
  const drupalPaths = await getPathsFromContext("node--event", context);

  return {
    paths: fixPathsSlug(drupalPaths),
    fallback: true,
  };
}

 async function _getStaticProps(context) {
  const globals = await getGlobals(context);
  const path = getPathFromContext(context, "node--event");
  const params = new DrupalJsonApiParams();
  const now = formatDateYMD(new Date());

  const node = await getResourceByPath(`${path}/${context.params.slug}`, {
    params: params.addInclude(["field_image", "field_type"]).getQueryObject(),
    locale: context.locale,
    defaultLocale: context.defaultLocale,
  });

  if (!context.preview && !node?.status) {
    return {
      notFound: true,
      revalidate: 30,
    };
  }

  const nodes = await getResourceCollectionFromContext("node--event", context, {
    params: params
      .addGroup("forthcoming", "OR")
      .addFilter("status", 1)
      .addFilter("id", node.id, "<>")
      .addFilter("field_date_end", now, ">=", "forthcoming")
      .addFilter("field_date_start", now, ">=", "forthcoming")
      .addFilter("langcode", context.locale)
      .addPageLimit(3)
      .addSort("sticky", "DESC")
      .addSort("field_date_start", "ASC")
      .getQueryObject(),
  });

  return {
    props: {
      crumbs: getCrumbs(context, node, globals.menus.crumbsFallback),
      globals,
      node,
      nodes,
    },
    revalidate: 30,
  };
}


    export async function getStaticProps(ctx) {
        let res = _getStaticProps(ctx)
        if(typeof res.then === 'function') res = await res
        return {
          
          ...res,
          props: {
            ...(res.props || {}),
            ...(await __loadNamespaces({
              ...ctx,
              pathname: '/node--event/[slug]',
              loaderName: 'getStaticProps',
              ...__i18nConfig,
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
            }))
          }
        }
    }
  